import React, { useMemo, useState } from 'react';
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form';

import { useSellerProducts } from '@/clients/sellerProducts/useSellerProduct.ts';
import { SellerProduct } from '@/clients/sellerProducts/SellerProduct.ts';
import { useTranslation } from 'react-i18next';
import { SearchableCombobox } from '@/shared/ui/Combobox/SearchableCombobox.tsx';
import Fuse from 'fuse.js';

type SellerProductReference = {
    sellerProductId: string;
    productDefinitionId: string;
    legacyFactoryId?: number;
    name: string;
};

type SellerProductFieldValues = {
    [key: string]: SellerProductReference | null;
};

export type SelectSellerProductInputProps = Pick<
    UseControllerProps<SellerProductFieldValues, string>,
    'name' | 'defaultValue'
> & {
    label?: string;
    legacyFactoryId?: number;
    placeholder?: string;
    dataTest?: string;
    disabled?: boolean;
    className?: string;
    defaultValueLabel?: string;
    /** `sellerProduct` is the reference stored in the form, `catalogueProduct` the full API item it was picked from. */
    onValueChange?: (
        sellerProduct: SellerProductReference | undefined,
        catalogueProduct: SellerProduct | undefined
    ) => void;
};

export const SelectSellerProductInput = React.forwardRef<HTMLInputElement, SelectSellerProductInputProps>(
    (
        { name, legacyFactoryId, defaultValue, placeholder, label, disabled, className, onValueChange, dataTest },
        _ref
    ) => {
        const { t } = useTranslation();

        const { control } = useFormContext();
        const [search, setSearch] = useState('');

        const { data: currentSellerProduct, isLoading } = useSellerProducts(legacyFactoryId ?? null);

        const fuse = useMemo(
            () =>
                new Fuse(currentSellerProduct?.items ?? [], {
                    keys: [{ name: 'name', weight: 1 }],
                }),
            [currentSellerProduct]
        );

        const options = useMemo(() => {
            const filtered = search ? fuse.search(search).map(r => r.item) : currentSellerProduct?.items;

            if (filtered === undefined) {
                return [];
            }

            return filtered.map(item => ({
                label: item.name,
                value: item.id,
                productDefinitionId: item.productDefinitionId,
                catalogueProduct: item,
            }));
        }, [currentSellerProduct, search, fuse]);

        return (
            <Controller
                name={name}
                defaultValue={defaultValue ?? null}
                control={control}
                render={({ field: { ref, onChange, value } }) => (
                    <SearchableCombobox
                        fieldRef={ref}
                        value={value?.sellerProductId}
                        onValueChange={newValue => {
                            // Prevent toggle-deselect: clicking the already-selected item should not clear the field
                            if (newValue === undefined || legacyFactoryId === undefined) return;

                            const option = options.find(option => option.value === newValue);
                            const nextValue: SellerProductReference | undefined =
                                option !== undefined
                                    ? {
                                          sellerProductId: option.value,
                                          productDefinitionId: option.productDefinitionId,
                                          legacyFactoryId: legacyFactoryId,
                                          name: option.label,
                                      }
                                    : undefined;

                            onValueChange?.(nextValue, option?.catalogueProduct);
                            onChange(nextValue);
                        }}
                        options={options}
                        disabled={disabled}
                        placeholder={placeholder}
                        label={label ?? t('forms.inputs.sellerProductInput.label')}
                        size="lg"
                        className={className}
                        dataTest={dataTest}
                        loading={isLoading}
                        search={search}
                        onSearchChange={setSearch}
                    />
                )}
            />
        );
    }
);
